// WALLET-P1 §6.1: Add funds, a modal. Preset chips from the bonus ladder ("$500 +10%"), a custom
// amount with the limits in its description, the credit that will land ("You get $550", integer
// math over the ladder) and Continue → a Stripe Checkout Session (`wallet.topUp`), the browser
// sent there. No card form of our own. Disabled with the reason while payments are not set up.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../ops-parts";
import { bonusFor, bpText, dollars, MICROS_PER_DOLLAR, parseDollars } from "./billing-money";
import { DisabledWhy } from "./billing-parts";
import type { WalletView } from "./billing-queries";

/** The preset amounts: $50 / $100 when the minimum allows, then every ladder rung with a bonus. */
export function presetsOf(
  wallet: Pick<WalletView, "ladder" | "min_topup_micros" | "max_topup_micros">,
) {
  const amounts = new Set<number>();
  for (const a of [50, 100]) {
    const m = a * MICROS_PER_DOLLAR;
    if (m >= wallet.min_topup_micros && m <= wallet.max_topup_micros) amounts.add(m);
  }
  for (const r of wallet.ladder)
    if (
      r.bonus_bp > 0 &&
      r.min_amount_micros >= wallet.min_topup_micros &&
      r.min_amount_micros <= wallet.max_topup_micros
    )
      amounts.add(r.min_amount_micros);
  return [...amounts]
    .sort((a, b) => a - b)
    .map((amount) => {
      const rung = wallet.ladder
        .filter((r) => r.min_amount_micros <= amount)
        .sort((a, b) => b.min_amount_micros - a.min_amount_micros)[0];
      const bonus = rung && rung.bonus_bp > 0 ? ` +${bpText(rung.bonus_bp)}` : "";
      return { amount, label: `${dollars(amount, { whole: true })}${bonus}` };
    });
}

export function AddFundsButton({
  api,
  company,
  wallet,
}: {
  api: Api;
  company: CompanyAccess;
  wallet: WalletView;
}) {
  const [open, setOpen] = useState(false);
  const button = (
    <Button size="sm" disabled={!wallet.checkout_ready} onClick={() => setOpen(true)}>
      <PlusIcon data-icon="inline-start" aria-hidden="true" />
      Add funds
    </Button>
  );
  if (!wallet.checkout_ready)
    return <DisabledWhy why="Payments aren't set up yet">{button}</DisabledWhy>;
  return (
    <>
      {button}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-slot="add-funds">
          {open ? <AddFundsForm api={api} company={company} wallet={wallet} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * One Idempotency-Key per top-up intent: the same amount in the same open dialog keeps its key
 * (a retry after a failed response recovers the same Checkout Session); a new amount, or a dialog
 * opened anew (the form remounts), gets a fresh one.
 */
export function useTopUpKey(): (amountMicros: number) => string {
  const last = useRef<{ amount: number; key: string } | null>(null);
  return (amountMicros) => {
    if (last.current?.amount !== amountMicros)
      last.current = { amount: amountMicros, key: newIdempotencyKey() };
    return last.current.key;
  };
}

function AddFundsForm({
  api,
  company,
  wallet,
}: {
  api: Api;
  company: CompanyAccess;
  wallet: WalletView;
}) {
  const presets = useMemo(() => presetsOf(wallet), [wallet]);
  const first = presets.find((p) => p.amount >= 500 * MICROS_PER_DOLLAR) ?? presets[0];
  const [picked, setPicked] = useState<number | null>(first?.amount ?? null);
  const [custom, setCustom] = useState("");
  const amountId = useId();
  const typed = custom.trim() === "" ? null : parseDollars(custom);
  const amount = custom.trim() !== "" ? typed : picked;
  const tooLow = amount !== null && amount < wallet.min_topup_micros;
  const tooHigh = amount !== null && amount > wallet.max_topup_micros;
  const invalid = custom.trim() !== "" && (typed === null || typed <= 0);
  const ok = amount !== null && amount > 0 && !tooLow && !tooHigh && !invalid;
  const bonus = ok ? bonusFor(wallet.ladder, amount) : 0;
  const topUpKey = useTopUpKey();
  const start = useMutation({
    mutationFn: (amount_micros: number) =>
      api.client.wallet.topUp({
        params: { co: company.id },
        idempotencyKey: topUpKey(amount_micros),
        body: { amount_micros },
      }),
    onSuccess: (r) => window.location.assign(r.checkout_url),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok && amount !== null) start.mutate(amount);
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Add funds</DialogTitle>
      </DialogHeader>
      <fieldset className="flex flex-wrap gap-1.5" data-slot="presets">
        <legend className="sr-only">Amount</legend>
        {presets.map((p) => {
          const on = custom.trim() === "" && picked === p.amount;
          return (
            <button
              key={p.amount}
              type="button"
              aria-pressed={on}
              onClick={() => {
                setPicked(p.amount);
                setCustom("");
              }}
              className={cn(
                "inline-flex h-8 items-center rounded-md border px-3 text-[13px] font-medium tabular-nums transition-colors",
                on
                  ? "border-primary bg-primary-soft text-primary-text"
                  : "border-input bg-card text-secondary hover:bg-sunken",
              )}
            >
              {p.label}
            </button>
          );
        })}
      </fieldset>
      <Field data-invalid={invalid || tooLow || tooHigh}>
        <FieldLabel htmlFor={amountId}>Other amount ($)</FieldLabel>
        <Input
          id={amountId}
          inputMode="decimal"
          value={custom}
          placeholder="e.g. 750"
          aria-invalid={invalid || tooLow || tooHigh}
          onChange={(e) => setCustom(e.target.value)}
        />
        <FieldDescription>
          Minimum {dollars(wallet.min_topup_micros)}, maximum {dollars(wallet.max_topup_micros)}.
        </FieldDescription>
        {invalid ? (
          <FieldError>Enter an amount like 750 or 750.50.</FieldError>
        ) : tooLow ? (
          <FieldError>At least {dollars(wallet.min_topup_micros)}.</FieldError>
        ) : tooHigh ? (
          <FieldError>At most {dollars(wallet.max_topup_micros)}.</FieldError>
        ) : null}
      </Field>
      <p className="text-sm" data-slot="you-get" aria-live="polite">
        {ok && amount !== null ? (
          <>
            You get <span className="font-semibold tabular-nums">{dollars(amount + bonus)}</span>
            {bonus > 0 ? (
              <span className="text-muted-foreground">
                {" "}
                ({dollars(amount)} + {dollars(bonus)} bonus)
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-muted-foreground">Pick or enter an amount.</span>
        )}
      </p>
      {start.isError ? (
        <FieldError>{problemText(start.error, "Checkout could not be started.")}</FieldError>
      ) : null}
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!ok || start.isPending}>
          {start.isPending ? "Opening checkout…" : "Continue"}
        </Button>
      </DialogFooter>
    </form>
  );
}
